import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PATHS } from "../../data/content";
import { SERVICES } from "../../data/services";
import { IMG } from "../../data/site";
import { Reveal } from "../Motion";
import { Img } from "../Img";
import { Eyebrow, TextLink } from "../Bits";

export const PathsSection = () => (
  <section aria-labelledby="paths-h" className="bg-bone py-28" data-testid="buyer-paths">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Eyebrow>Start with your situation</Eyebrow>
      <h2 id="paths-h" className="h2-display mt-3 max-w-3xl">You don't need to know the part name. Start with what you're noticing.</h2>
      <div className="mt-16 space-y-20">
        {PATHS.map((p, i) => (
          <Reveal key={p.n}>
            <article className={`grid items-center gap-10 lg:grid-cols-2 ${i % 2 ? "lg:[&>*:first-child]:order-2" : ""}`} data-testid={`path-card-${p.n}`}>
              <div className="clip-frame-light">
                <Img img={p.img} className="aspect-[16/10] w-full" />
              </div>
              <div>
                <span className="font-mono text-sm text-flame-deep">Path {p.n}</span>
                <h3 className="mt-3 font-display text-4xl uppercase leading-[0.95] tracking-tight">{p.situation}</h3>
                <dl className="mt-6 space-y-4 border-l-2 border-flame-deep pl-5">
                  <div><dt className="eyebrow text-slate2">What it usually means</dt><dd className="mt-1 text-slate2">{p.understanding}</dd></div>
                </dl>
                <div className="mt-8"><TextLink to={p.to} testid={`path-link-${p.n}`}>{p.action}</TextLink></div>
              </div>
            </article>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Tile = ({ s, className, big }) => (
  <Link to={s.path} className={`bento-tile group ${className}`} data-testid={`service-tile-${s.slug}`}>
    <Img img={s.hero} label={false} className="absolute inset-0 h-full w-full transition-transform duration-700 group-hover:scale-105" sizes={big ? "(min-width:1024px) 60vw, 100vw" : "(min-width:1024px) 30vw, 100vw"} />
    <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/45 to-transparent" aria-hidden="true" />
    <div className="relative mt-auto p-6 sm:p-8">
      <span className="eyebrow text-bone/90">{s.tier} · {s.kicker}</span>
      <h3 className={`mt-2 font-display uppercase leading-none tracking-tight text-bone ${big ? "text-4xl sm:text-5xl" : "text-3xl"}`}>{s.short}</h3>
      {big && <p className="mt-3 max-w-md text-bone/90">{s.summary}</p>}
      <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-bone">Read the {s.short.toLowerCase()} guide <ArrowRight aria-hidden="true" className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
    </div>
  </Link>
);

export const ServicesBento = () => {
  const [repair, replace, maint, hp, co] = SERVICES;
  return (
    <section aria-labelledby="svc-h" className="bg-fog py-28 text-ink" data-testid="services-bento">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <Eyebrow light>Services, in order of what Denver homes need most</Eyebrow>
            <h2 className="mt-3 font-display text-5xl uppercase leading-[0.95] tracking-tight sm:text-6xl" id="svc-h">Repair first. Replace when it's right.</h2>
          </div>
          <TextLink to="/services" testid="all-services-link">View all services</TextLink>
        </div>
        <div className="mt-14 grid auto-rows-[minmax(260px,auto)] gap-4 md:grid-cols-6 lg:auto-rows-[300px]">
          <Tile s={repair} big className="md:col-span-6 lg:col-span-4 lg:row-span-2" />
          <Tile s={replace} className="md:col-span-3 lg:col-span-2" />
          <Tile s={maint} className="md:col-span-3 lg:col-span-2" />
          <Tile s={hp} className="md:col-span-3 lg:col-span-3" />
          <Tile s={co} className="md:col-span-3 lg:col-span-3" />
        </div>
      </div>
    </section>
  );
};

const ALT = [
  { k: "Thinner air", d: "About 17% less air pressure than sea level. Gas appliances should be set up per the manufacturer's high-altitude instructions." },
  { k: "Dry winters", d: "Low humidity loads filters with dust faster and makes rooms feel cooler at the same temperature." },
  { k: "Sharp cold fronts", d: "Mild days can turn to arctic nights quickly. Weak furnaces tend to fail on the first long, cold run." },
  { k: "Mixed housing stock", d: "From 1920s bungalows to new builds: duct layouts and venting vary widely across the metro." },
];

export const AltitudeSection = () => (
  <section aria-labelledby="alt-h" className="bg-bone py-28" data-testid="altitude-section">
    <div className="mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-2 lg:items-center">
      <Reveal>
        <Eyebrow>Local context that changes the work</Eyebrow>
        <h2 id="alt-h" className="h2-display mt-3">What 5,280 feet does to a furnace</h2>
        <ul className="mt-10 grid gap-6 sm:grid-cols-2">
          {ALT.map((a) => (
            <li key={a.k} className="border-t border-ink/15 pt-4">
              <h3 className="font-display text-2xl uppercase tracking-tight">{a.k}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate2">{a.d}</p>
            </li>
          ))}
        </ul>
      </Reveal>
      <Reveal delay={0.1}>
        <div className="clip-frame-light">
          <Img img={IMG.skyline} className="aspect-[4/3] w-full" />
        </div>
      </Reveal>
    </div>
  </section>
);
